/**
 * Lock de personalización mientras Calvin trabaja.
 *
 * El problema que resuelve: guardar desde el admin hace PUT de la copia que el
 * navegador tiene en memoria. Si Calvin deployó mientras tanto, ese guardado lo
 * revierte sin avisar. Pasó en producción — el mismo cambio se revirtió dos
 * veces y el usuario siguió viendo lo que había pedido borrar.
 *
 * Cómo se hace cumplir, en dos lugares:
 *
 *  1. Acá, en el cliente: el chequeo vive en la capa de request
 *     (services/baseRequest), por donde pasan TODOS los guardados del admin.
 *     Un botón nuevo mañana queda cubierto sin que nadie se acuerde de este
 *     archivo. Sirve para dar un mensaje claro sin ir al servidor.
 *  2. En admin-apis, con un middleware sobre las rutas de contenido. Esa es la
 *     que vale: el front puede estar viejo, cacheado o ser otro cliente.
 *
 * El lock es del CONTENIDO entero: si Calvin está trabajando, no se toca
 * ninguna variante ni experiencia de esa personalización.
 */
import Vue from "vue";
import { getActiveCompanyId } from "@/helpers/companySession";

// Reactivo: los componentes se bindean a esto y se actualizan solos.
export const contentLockState = Vue.observable({
  contentId: null,
  lock: null,
});

const POLL_MS = 15000;
let pollTimer = null;
let taskDoneHandler = null;

function backend() {
  return (typeof window !== "undefined" && window.config && window.config.VUE_APP_BACKEND) || "";
}

// Mismo par que arma services/baseRequest: la sesión y la cuenta de esta pestaña.
function authHeader() {
  try {
    const stored = JSON.parse(localStorage.getItem("auth.currentUser"));
    if (!stored || !stored.token) return {};
    const company = getActiveCompanyId();
    return {
      authorization: `Bearer ${stored.token}`,
      ...(company ? { "x-company": company } : {}),
    };
  } catch (e) {
    return {};
  }
}

/** Una consulta puntual del estado del lock. */
export async function refreshContentLock() {
  const contentId = contentLockState.contentId;
  if (!contentId) return null;

  try {
    const res = await fetch(`${backend()}/api/contents/${contentId}/lock`, {
      headers: authHeader(),
    });
    if (!res.ok) return contentLockState.lock;
    const body = await res.json();
    const lock = (body && body.data && body.data.lock) || null;
    // Sólo escribir si cambió: evita re-renders cada 15s.
    if (JSON.stringify(lock) !== JSON.stringify(contentLockState.lock)) {
      contentLockState.lock = lock;
    }
    return lock;
  } catch (e) {
    // Sin red no inventamos un lock: el servidor sigue siendo la autoridad.
    return contentLockState.lock;
  }
}

/**
 * Empieza a seguir el lock de una personalización. Poléa porque el lock lo
 * toma otro proceso (el chat, del lado del servidor) y puede aparecer mientras
 * la pantalla ya está abierta; y se refresca además cuando una tarea termina,
 * para que el banner se vaya en el acto y no a los 15 segundos.
 */
export function watchContentLock(contentId) {
  stopWatchingContentLock();
  if (!contentId) return;

  contentLockState.contentId = contentId;
  refreshContentLock();
  pollTimer = setInterval(refreshContentLock, POLL_MS);

  taskDoneHandler = () => refreshContentLock();
  window.addEventListener("gopersonal-chat:calvin-task-done", taskDoneHandler);
}

export function stopWatchingContentLock() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  if (taskDoneHandler) {
    window.removeEventListener("gopersonal-chat:calvin-task-done", taskDoneHandler);
    taskDoneHandler = null;
  }
  contentLockState.contentId = null;
  contentLockState.lock = null;
}

/**
 * Destraba la personalización a mano.
 *
 * Existe porque un contenedor de Calvin que muere sin liberar deja la
 * personalización trabada y no queda nadie vivo que pueda soltarla. Antes que
 * dejar a alguien esperando el vencimiento, que pueda hacerlo a sabiendas: la
 * pantalla le avisa lo que se está saltando.
 */
export async function forceUnlockContent() {
  const contentId = contentLockState.contentId;
  if (!contentId) return false;

  try {
    const res = await fetch(`${backend()}/api/contents/${contentId}/lock?force=true`, {
      method: "DELETE",
      headers: authHeader(),
    });
    if (!res.ok) return false;
    contentLockState.lock = null;
    return true;
  } catch (e) {
    return false;
  }
}

export function isContentLocked() {
  return !!contentLockState.lock;
}

/** Texto corto para botones y tooltips. */
export function contentLockReason() {
  const lock = contentLockState.lock;
  if (!lock) return "";
  return lock.taskName
    ? `Calvin está trabajando en esta personalización: «${lock.taskName}»`
    : "Calvin está trabajando en esta personalización";
}

/**
 * ¿Esta request hay que frenarla? Sólo escrituras sobre el contenido tomado.
 * Las lecturas siempre pasan, y el endpoint del propio lock también.
 */
export function contentLockBlocks(method, url) {
  const lock = contentLockState.lock;
  const contentId = contentLockState.contentId;
  if (!lock || !contentId) return false;
  if (!["post", "put", "delete", "patch"].includes(String(method).toLowerCase())) return false;

  const path = String(url || "");
  if (path.includes("/lock")) return false;
  // Restaurar una versión del historial pega a /api/contents/history con el
  // contenido en el body: no lleva el id en la URL, pero escribe igual.
  if (path.includes("/api/contents/history")) return true;
  if (!path.includes(`/api/contents/${contentId}`)) return false;

  return true;
}

/** El aviso, siempre el mismo, venga del cliente o del 423 del servidor. */
export function notifyContentLock(lock) {
  const name = lock && lock.taskName ? `: «${lock.taskName}»` : "";
  if (Vue.notify) {
    Vue.notify({
      type: "warn",
      title: "Calvin",
      text: `Calvin está trabajando en esta personalización${name}. No se puede guardar hasta que termine.`,
    });
  }
}

/** Error con la misma forma que devuelve el servidor, para tratarlos igual. */
export function contentLockError() {
  notifyContentLock(contentLockState.lock);
  return {
    isContentLock: true,
    response: {
      status: 423,
      data: {
        error: "CONTENT_LOCKED",
        message: contentLockReason(),
        lock: contentLockState.lock,
      },
    },
  };
}

/**
 * El servidor rechazó por lock. Sincronizamos lo que muestra la pantalla y
 * avisamos: esto llega también desde pantallas que no siguen el lock (la lista
 * de personalizaciones, por ejemplo), donde si no el guardado fallaría mudo.
 */
export function noteServerLock(lock) {
  if (lock) contentLockState.lock = lock;
  else refreshContentLock();
  notifyContentLock(lock || contentLockState.lock);
}
